// ----------------------------------------------------------------------------
// akima.js — Akima cubic spline interpolation
//
// Ported from https://github.com/chdh/commons-math-interpolation (Apache 2.0)
// Based on: "A New Method of Interpolation and Smooth Curve Fitting Based on
// Local Procedures" by Hiroshi Akima, J. ACM 17, 4 (October 1970).
//
// Akima splines pass through all knots exactly and do not overshoot,
// making them ideal for automation curve editing.
//
// Fallbacks:
//   >= 5 knots: Akima cubic spline
//   3-4 knots: Natural cubic spline
//   2 knots:   Linear interpolation
// ----------------------------------------------------------------------------

var EPSILON = Number.EPSILON;

// ---- Evaluate polynomial ----
// c: coefficients in ascending order [c0, c1, c2, c3]
// x: offset from segment start
function evaluatePoly(c, x) {
   var n = c.length;
   if (n === 0) return 0;
   var v = c[n - 1];
   for (var i = n - 2; i >= 0; i--) {
      v = x * v + c[i];
   }
   return v;
}

// ---- Binary search for segment index ----
function binarySearch(a, key) {
   var low = 0, high = a.length - 1;
   while (low <= high) {
      var mid = (low + high) >>> 1;
      var midVal = a[mid];
      if (midVal < key) low = mid + 1;
      else if (midVal > key) high = mid - 1;
      else return mid;
   }
   return -(low + 1);
}

// ---- Evaluate polynomial segment for x ----
function evaluatePolySegment(xVals, segmentCoeffs, x) {
   var i = binarySearch(xVals, x);
   if (i < 0) i = -i - 2;
   if (i < 0) i = 0;
   if (i >= segmentCoeffs.length) i = segmentCoeffs.length - 1;
   return evaluatePoly(segmentCoeffs[i], x - xVals[i]);
}

// ---- Trim trailing zero coefficients ----
function trimPoly(c) {
   var n = c.length;
   while (n > 1 && c[n - 1] === 0) n--;
   return n === c.length ? c : c.subarray(0, n);
}

// ---- Three-point differentiation ----
function differentiateThreePoint(xVals, yVals, idxDiff, idx0, idx1, idx2) {
   var x0 = yVals[idx0], x1 = yVals[idx1], x2 = yVals[idx2];
   var t  = xVals[idxDiff] - xVals[idx0];
   var t1 = xVals[idx1]    - xVals[idx0];
   var t2 = xVals[idx2]    - xVals[idx0];
   var a = (x2 - x0 - (t2 / t1 * (x1 - x0))) / (t2 * t2 - t1 * t2);
   var b = (x1 - x0 - a * t1 * t1) / t1;
   return (2 * a * t) + b;
}

// ---- Compute Hermite polynomial coefficients ----
function computeHermitePolyCoefficients(xVals, yVals, firstDerivatives) {
   var n = xVals.length - 1;
   var segmentCoeffs = new Array(n);
   for (var i = 0; i < n; i++) {
      var w = xVals[i + 1] - xVals[i];
      var w2 = w * w;
      var yv = yVals[i], yvP = yVals[i + 1];
      var fd = firstDerivatives[i], fdP = firstDerivatives[i + 1];
      var coeffs = new Float64Array(4);
      coeffs[0] = yv;
      coeffs[1] = fd;
      coeffs[2] = (3 * (yvP - yv) / w - 2 * fd - fdP) / w;
      coeffs[3] = (2 * (yv - yvP) / w + fd + fdP) / w2;
      segmentCoeffs[i] = trimPoly(coeffs);
   }
   return segmentCoeffs;
}

// ---- Compute Akima polynomial coefficients ----
// Requires >= 5 knots, xVals strictly increasing
function computeAkimaPolyCoefficients(xVals, yVals) {
   var n = xVals.length - 1;
   var differences = new Float64Array(n);
   var weights = new Float64Array(n);
   for (var i = 0; i < n; i++) {
      differences[i] = (yVals[i + 1] - yVals[i]) / (xVals[i + 1] - xVals[i]);
   }
   for (var i = 1; i < n; i++) {
      weights[i] = Math.abs(differences[i] - differences[i - 1]);
   }
   var firstDerivatives = new Float64Array(n + 1);
   for (var i = 2; i < n - 1; i++) {
      var wP = weights[i + 1], wM = weights[i - 1];
      if (Math.abs(wP) < EPSILON && Math.abs(wM) < EPSILON) {
         var xv = xVals[i], xvP = xVals[i + 1], xvM = xVals[i - 1];
         firstDerivatives[i] = (((xvP - xv) * differences[i - 1]) + ((xv - xvM) * differences[i])) / (xvP - xvM);
      } else {
         firstDerivatives[i] = ((wP * differences[i - 1]) + (wM * differences[i])) / (wP + wM);
      }
   }
   firstDerivatives[0]     = differentiateThreePoint(xVals, yVals, 0, 0, 1, 2);
   firstDerivatives[1]     = differentiateThreePoint(xVals, yVals, 1, 0, 1, 2);
   firstDerivatives[n - 1] = differentiateThreePoint(xVals, yVals, n - 1, n - 2, n - 1, n);
   firstDerivatives[n]     = differentiateThreePoint(xVals, yVals, n,     n - 2, n - 1, n);
   return computeHermitePolyCoefficients(xVals, yVals, firstDerivatives);
}

// ---- Compute natural cubic spline coefficients ----
// Requires >= 3 knots, xVals strictly increasing
function computeCubicPolyCoefficients(xVals, yVals) {
   var n = xVals.length - 1;
   var h = new Float64Array(n);
   for (var i = 0; i < n; i++) h[i] = xVals[i + 1] - xVals[i];
   var mu = new Float64Array(n);
   var z = new Float64Array(n + 1);
   mu[0] = 0; z[0] = 0;
   for (var i = 1; i < n; i++) {
      var g = 2 * (xVals[i + 1] - xVals[i - 1]) - h[i - 1] * mu[i - 1];
      mu[i] = h[i] / g;
      z[i] = (3 * (yVals[i + 1] * h[i - 1] - yVals[i] * (xVals[i + 1] - xVals[i - 1]) + yVals[i - 1] * h[i]) /
             (h[i - 1] * h[i]) - h[i - 1] * z[i - 1]) / g;
   }
   var b = new Float64Array(n);
   var c = new Float64Array(n + 1);
   var d = new Float64Array(n);
   z[n] = 0; c[n] = 0;
   for (var i = n - 1; i >= 0; i--) {
      var dx = h[i];
      var dy = yVals[i + 1] - yVals[i];
      c[i] = z[i] - mu[i] * c[i + 1];
      b[i] = dy / dx - dx * (c[i + 1] + 2 * c[i]) / 3;
      d[i] = (c[i + 1] - c[i]) / (3 * dx);
   }
   var segmentCoeffs = new Array(n);
   for (var i = 0; i < n; i++) {
      var coeffs = new Float64Array(4);
      coeffs[0] = yVals[i];
      coeffs[1] = b[i];
      coeffs[2] = c[i];
      coeffs[3] = d[i];
      segmentCoeffs[i] = trimPoly(coeffs);
   }
   return segmentCoeffs;
}

// ---- Create interpolator function from points ----
// points: array of { x, y } with strictly increasing x
// Returns: function(x) -> interpolated y value
function createInterpolator(points) {
   if (!points || points.length < 2) return null;

   // Extract x and y arrays
   var xVals = new Float64Array(points.length);
   var yVals = new Float64Array(points.length);
   for (var i = 0; i < points.length; i++) {
      xVals[i] = points[i].x;
      yVals[i] = points[i].y;
   }

   var segmentCoeffs;
   if (points.length >= 5) {
      segmentCoeffs = computeAkimaPolyCoefficients(xVals, yVals);
   } else if (points.length >= 3) {
      segmentCoeffs = computeCubicPolyCoefficients(xVals, yVals);
   } else {
      // Linear interpolation for 2 points
      segmentCoeffs = [new Float64Array([yVals[0], (yVals[1] - yVals[0]) / (xVals[1] - xVals[0])])];
   }

   return function(x) {
      // Clamp to range
      if (x <= xVals[0]) return yVals[0];
      if (x >= xVals[xVals.length - 1]) return yVals[yVals.length - 1];
      return evaluatePolySegment(xVals, segmentCoeffs, x);
   };
}

// ---- Sample the curve at N points for drawing ----
// points: array of { x, y }
// samples: number of samples to generate
// Returns: array of { x, y } sampled points
function sampleCurve(points, samples) {
   var interp = createInterpolator(points);
   if (!interp || points.length < 2) return points.slice();
   var result = new Array(samples);
   var xMin = points[0].x;
   var xMax = points[points.length - 1].x;
   for (var i = 0; i < samples; i++) {
      var t = i / (samples - 1);
      var x = xMin + t * (xMax - xMin);
      result[i] = { x: x, y: interp(x) };
   }
   return result;
}

module.exports = {
   createInterpolator,
   sampleCurve,
   evaluatePoly,
   evaluatePolySegment,
};
